import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

type EntityId = string | number;

interface RequestArgs {
  endpoint: string;
  id?: EntityId;
  credentials?: RequestCredentials;
}

interface MutationArgs extends RequestArgs {
  body: unknown;
}

const apiUrl = process.env.REACT_APP_API_URL?.trim().replace(/\/$/, "") || "";
const api = `${apiUrl}/api`;

const buildApiUrl = (endpoint: string) =>
  `${api}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;

const appendId = (endpoint: string, id?: EntityId) => {
  if (id === undefined || id === null || id === "") {
    return endpoint;
  }

  return `${endpoint.replace(/\/$/, "")}/${id}`;
};

export const apiSlice = createApi({
  reducerPath: "apiSlice",
  baseQuery: fetchBaseQuery({
    baseUrl: api,
    prepareHeaders: (headers) => {
      headers.set("Accept", "application/json");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    get: builder.query<unknown, RequestArgs>({
      query: ({ endpoint, credentials }) => ({
        url: endpoint,
        method: "GET",
        credentials,
      }),
    }),
    add: builder.mutation<unknown, MutationArgs>({
      query: ({ endpoint, credentials, id, body }) => ({
        url: appendId(endpoint, id),
        method: "POST",
        credentials,
        body,
      }),
    }),
    update: builder.mutation<unknown, MutationArgs>({
      query: ({ endpoint, credentials, id, body }) => ({
        url: appendId(endpoint, id),
        method: "PUT",
        credentials,
        body,
      }),
    }),
    remove: builder.mutation<unknown, RequestArgs>({
      query: ({ endpoint, credentials, id }) => ({
        url: appendId(endpoint, id),
        method: "DELETE",
        credentials,
      }),
    }),
  }),
});

export const {
  useLazyGetQuery,
  useAddMutation,
  useUpdateMutation,
  useRemoveMutation,
} = apiSlice;

export { apiUrl as url, api, buildApiUrl as buildUrl };
